import {
  Close,
  Delete,
  DeleteOutline,
  Edit,
  ExpandLess,
  ExpandMore,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Select,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

// Extend dayjs with plugins
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
import { NoDataSvg } from "../../../clinicalNotesClinicalRoute/ClinicalNoteSVGs";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../../../../firebaseConfig";
import { Typography } from "@material-ui/core";
import Cookies from "js-cookie";
import UploadDocumentsfromExperts from "./UploadDocumentsfromExperts";
import { useAuth } from "../../../../../auth";
import { AddDocument, SendUploadedDoc } from "../_request";

// Helper function to parse various date formats
const parseDate = (dateString) => {
  if (!dateString) return null;

  try {
    // Trim whitespace and convert to string
    const trimmed = String(dateString).trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;

    // Try ISO 8601 format first (default dayjs parsing handles this)
    // This handles: 2025-01-13T21:48:54.132+00:00
    let parsed = dayjs(trimmed);
    if (parsed.isValid()) {
      return parsed;
    }

    // Try with UTC parsing for ISO formats with timezone
    parsed = dayjs.utc(trimmed);
    if (parsed.isValid()) {
      return parsed;
    }

    // Try format: DD/MM/YYYY, HH:mm:ss or MM/DD/YYYY, HH:mm:ss
    // This handles: 11/04/2025, 05:03:11
    const formats = [
      "DD/MM/YYYY, HH:mm:ss",
      "MM/DD/YYYY, HH:mm:ss",
      "D/M/YYYY, HH:mm:ss",
      "M/D/YYYY, HH:mm:ss",
      "DD/MM/YYYY, H:mm:ss",
      "MM/DD/YYYY, H:mm:ss",
      "DD/MM/YYYY HH:mm:ss",
      "MM/DD/YYYY HH:mm:ss",
      "DD/MM/YYYY, HH:mm",
      "MM/DD/YYYY, HH:mm",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY-MM-DDTHH:mm:ss.SSS[+00:00]",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ss[+00:00]",
      "YYYY-MM-DDTHH:mm:ssZ",
      "YYYY-MM-DDTHH:mm:ss.SSS",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD",
    ];

    // Try with strict mode first
    for (const format of formats) {
      parsed = dayjs(trimmed, format, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // Try with lenient parsing (without strict mode)
    for (const format of formats) {
      parsed = dayjs(trimmed, format);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    // If all parsing fails, try native Date parsing as fallback
    const nativeDate = new Date(trimmed);
    if (!isNaN(nativeDate.getTime())) {
      return dayjs(nativeDate);
    }

    // Try parsing as timestamp (if it's a number string)
    if (!isNaN(trimmed) && !isNaN(parseFloat(trimmed))) {
      const timestamp = parseFloat(trimmed);
      parsed = dayjs(timestamp);
      if (parsed.isValid()) {
        return parsed;
      }
    }
  } catch (error) {
    console.error("Error parsing date:", dateString, error);
  }

  return null;
};

function Documents({ clientDetails }) {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();


  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debounce, setDebounce] = useState("");
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState("");
  const [newFileName, setNewFileName] = useState("");
  const [fileExtension, setFileExtension] = useState("");

  const [fileUploaded, setFileUploaded] = useState("idle");
  const [uploadedFileLink, setUploadedFileLink] = useState(null);
  const storedData = localStorage.getItem("Authorization");
  const token = storedData ? storedData?.split("Bearer ")[1] : null;

  const [isRenaming, setIsRenaming] = useState(false);


  useEffect(() => {
    if (clientDetails) {
      const profile = JSON.parse(localStorage.getItem("profile"));

      FetchClinicalNotesClients(clientDetails);
    }
  }, [clientDetails, page, selectedDocument, debounce]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebounce(searchTerm);
    }, 500);
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchTerm]);

  const DocTypes = {
    png: `${process.env.REACT_APP_DOC_BASE_URL}odoo/open/clinical-copilot/IMG2File.png`,
    doc: `${process.env.REACT_APP_DOC_BASE_URL}odoo/open/clinical-copilot/DOCFile.png`,
    excel: `${process.env.REACT_APP_DOC_BASE_URL}odoo/open/clinical-copilot/EXCELFile.png`,
    pdf: `${process.env.REACT_APP_DOC_BASE_URL}odoo/open/clinical-copilot/PDFFile.png`,
  };



  const FetchClinicalNotesClients = async (clientDetails) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CAUTH_URL}user-prescriptions?count=10&page=${page}&user_id=${clientDetails?.id}&category=${selectedDocument}&search=${debounce}`,
        {
          method: "GET",
          headers: {
            "X-API-Key": `${process.env.REACT_APP_CAUTH_X_API_KEY}`,
            "Content-Type": "application/json",
            token: token,
            authenticationToken: Cookies.get("authDataJwt"),
          },
        }
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }


      const resp = await response.json();
      console.log('response', resp);
      if (resp.status === 200) {
        setDocuments(resp.data);
        setTotalPages(resp.meta.total_pages);
      } else {
        enqueueSnackbar(resp.message, { variant: "info" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // console.log(clientDetails)
  console.log('documents', documents);



  const handleChange = (event, value) => {
    setPage(value);
  };

  const handleUploadExpertDocs = async (file, fileType) => {
    try {
      //const dirName = dayjs().format("MMMM_YYYY");
      const storageRef = ref(
        storage,
        `/clinic-data-storage/clients/${currentUser?.Country}/${dayjs().format(
          "YYYY-MMM-DD_HH:mm:ss"
        )}_${file.name}`
      );
      const uploadTask = uploadBytesResumable(storageRef, file);

      setFileUploaded("uploading");

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          console.log(`Upload progress: ${percent}%`);
        },
        (error) => {
          console.error("Upload error:", error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);

            console.log("URL obtained:", url);

            // Local variable to handle logic immediately
            let fileStatus;
            setUploadedFileLink(url);

            if (fileType === "FILE") {
              fileStatus = "uploaded";
              setFileUploaded(fileStatus);
            } else {
              fileStatus = url;
              setFileUploaded(fileStatus);
            }

            console.log(
              "fileUploaded (local):",
              fileStatus,
              "uploadedFileLink:",
              url
            );

            // Use the local variable for logic instead of relying on state
            if (fileStatus === "uploaded") {
              sendDataToSTT(url);
            }
          } catch (error) {
            console.error("Error getting download URL:", error);
          }
        }
      );
    } catch (error) {
      console.error("Error while uploading", error);
      setFileUploaded("idle");

      setUploadedFileLink(null);
    }
  };

  const sendDataToSTT = async (uploadedFileLink) => {
    try {
      const body = {
        firebase_url: uploadedFileLink,
        system_prompt: "",
      };

      const responseData = await SendUploadedDoc(JSON.stringify(body));

      if (responseData?.status == 200) {
        const addBody = {
          uploaded_by: "expert",
          user_id: clientDetails?.id + "", // Make sure to use clientDetails.id
          creator_id: currentUser?.UserId + "",
          clinical_note_id: null,
          doctor_names: responseData?.data?.doctor_names || "",
          response: responseData?.data?.response,
          clinical_relevance: responseData?.data?.clinical_relevance,
          metadata: responseData?.data?.metadata,
        };

        const addDocResponse = await AddDocument(addBody);

        if (addDocResponse?.data?.status === "success") {
          setFileUploaded("idle");
          setUploadedFileLink(null); // Reset the uploaded file link

          // Refresh the documents list - pass the full clientDetails object
          await FetchClinicalNotesClients(clientDetails);

          // Show success message
          enqueueSnackbar("Document uploaded successfully!", { variant: "success" });

          // Reset page to 1 to see the newly added document (if it appears at the top)
          setPage(1);
        } else {
          enqueueSnackbar("Failed to add document", { variant: "error" });
          setFileUploaded("idle");
        }
      } else {
        enqueueSnackbar("Failed to process document", { variant: "error" });
        setFileUploaded("idle");
      }
    } catch (error) {
      console.log("error", error);
      enqueueSnackbar("Error uploading document", { variant: "error" });
      setFileUploaded("idle");
      setUploadedFileLink(null);
    }
  };

  const handleRenameOpen = (file) => {
    const fileName = file?.metadata?.new_file_name || "";
    const lastDotIndex = fileName.lastIndexOf('.');
    
    if (lastDotIndex !== -1) {
      setNewFileName(fileName.substring(0, lastDotIndex));
      setFileExtension(fileName.substring(lastDotIndex));
    } else {
      setNewFileName(fileName);
      setFileExtension("");
    }
    
    setSelectedFile(file);
    setOpenRenameDialog(true);
  };

  const handleRenameClose = () => {
    setOpenRenameDialog(false);
    setSelectedFile(null);
    setNewFileName("");
    setFileExtension("");
  };
console.log(selectedFile)
  const handleRenameSubmit = async () => {
    try {
      setIsRenaming(true);
      const fullFileName = `${newFileName}${fileExtension}`;
      
      // Create new metadata object with updated file name
      const updatedMetadata = {
        ...selectedFile.metadata,
        new_file_name: fullFileName
      };
      

      const response = await fetch(
        `${process.env.REACT_APP_CAUTH_URL}user-prescriptions/${selectedFile._id}/update`,
        {
          method: "POST",
          headers: {
            "X-API-Key": `${process.env.REACT_APP_CAUTH_X_API_KEY}`,
            "Content-Type": "application/json",
            token: token,
            authenticationToken: Cookies.get("authDataJwt"),
          },
          body: JSON.stringify({
            metadata: updatedMetadata
          }),
        }
      );

      const data = await response.json();
      
      if (data.status === 200) {
        enqueueSnackbar("File renamed successfully!", { variant: "success" });
        await FetchClinicalNotesClients(clientDetails);
      } else {
        enqueueSnackbar(data.message || "Failed to rename file", { variant: "error" });
      }
    } catch (error) {
      console.error("Error renaming file:", error);
      enqueueSnackbar("Error renaming file", { variant: "error" });
    } finally {
      setIsRenaming(false);
      handleRenameClose();
    }
  };

  return (
    <div>
      <div className="d-flex my-3 gap-2 align-items-center justify-content-between">
        <Grid container spacing={2}>
          {/* Full-width header */}
          <Grid item xs={12} sm={4}>
            <div className="d-flex align-items-center gap-2">
              <TextField
                sx={{ minWidth: "150px" }}
                placeholder="Search"
                size="small"
                label=""
                value={searchTerm}
                disabled={selectedDocument}
                onChange={(e) => setSearchTerm(e.target.value)}
              />{" "}
              <Box sx={{ minWidth: "100px" }}>
                <FormControl
                  size="small"
                  fullWidth
                  sx={{ background: "white" }}
                  className=""
                >
                  <InputLabel id="demo-simple-select-label">Filter</InputLabel>
                  <Select
                    id="basic-menu"
                    onChange={(e) => {
                      setSelectedDocument(e.target.value);
                    }}
                    label="Filter"
                    value={selectedDocument}
                    disabled={debounce}
                  >
                    <MenuItem value="prescription">Prescription</MenuItem>
                    <MenuItem value="lab test">Lab Test</MenuItem>
                    <MenuItem value="treatment plans">Treatment Plans</MenuItem>
                    <MenuItem value="case history">Case History</MenuItem>
                    <MenuItem value="health monitoring data">
                      Health Monitoring Data
                    </MenuItem>
                    <MenuItem value="medication history">
                      Medication History
                    </MenuItem>
                    <MenuItem value="medical image">Medical Image</MenuItem>
                    <MenuItem value="surgical reports">
                      Surgical Reports
                    </MenuItem>
                    <MenuItem value="others">Others</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </div>
          </Grid>
          {/* Sidebar and Content */}
          <Grid item xs={12} sm={4}>
            {(selectedDocument || debounce) && (
              <div className="d-flex align-items-center gap-2 border rounded-3 p-2">
                {selectedDocument || debounce}

                <Close
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    setSelectedDocument("");
                    setSearchTerm("");
                  }}
                />
              </div>
            )}
          </Grid>
          {/* Footer */}
          <Grid item xs={12} sm={4} align="right">
            <div>
              <UploadDocumentsfromExperts
                handleUploadExpertDocs={handleUploadExpertDocs}
                fileUploaded={fileUploaded}
              />
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ maxWidth: "190px", paddingTop: "5px" }}
              >
                <span style={{ color: "red" }}>*</span>JPG, JPEG, PNG, PDF, up
                to 20MB.
              </Typography>
            </div>
          </Grid>
        </Grid>
      </div>

      <Dialog
        open={openRenameDialog}
        onClose={handleRenameClose}
        fullWidth
        maxWidth="sm"
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: "12px",
            backgroundColor: "#fff",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)"
          }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>Rename Document</DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              autoFocus
              fullWidth
              label="File Name"
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
            <Typography
              variant="body2"
              sx={{
                position: 'absolute',
                right: 14,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'text.secondary',
                pointerEvents: 'none',
                mt: 1
              }}
            >
              {fileExtension}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleRenameClose}
            sx={{
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRenameSubmit}
            disabled={!newFileName.trim() || newFileName === selectedFile?.metadata?.new_file_name.split('.')[0] || isRenaming}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              backgroundColor: 'var(--primary-color)',
              '&:hover': {
                backgroundColor: 'var(--primary-color)',
                opacity: 0.9
              }
            }}
          >
            {isRenaming ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <div className="row">
        {documents.map((item, index) => (
          <div className=" mb-4" key={index}>
            <div className="card">
              <div className="d-flex flex-column flex-md-row gap-2 align-items-center px-md-4 p-2">
                <div className="d-flex align-items-center  w-100  gap-2 gap-md-4 ">
                  <div>
                    <img
                      src={DocTypes[item?.metadata?.file_type] || DocTypes.png}
                      alt={`${item?.image_url} preview`}
                      className="card-img-top"
                      style={{
                        minWidth: "60px",
                        width: "60px",
                        height: "auto",
                      }} // Set a fixed width and auto height
                    />
                  </div>

                  <div style={{ color: "#344054" }} className="">
                    <span
                      style={{
                        fontSize: "14px",
                        background: "#DCFAE6",
                        color: "#079455",
                        border: "1px solid #079455",
                      }}
                      className="px-2 rounded-pill"
                    >
                      {item?.metadata?.category_name?.charAt(0)?.toUpperCase() +
                        item?.metadata?.category_name?.slice(1)}
                    </span>
                    <p className="m-0">
                      <strong>File Name:</strong> {item?.metadata.new_file_name}
                    </p>

                    <p className="m-0 ">
                      {(() => {
                        const parsedDate = parseDate(item?.metadata?.uploaded_datetime);
                        return parsedDate
                          ? parsedDate.format("MMM D, YYYY h:mm A")
                          : item?.uploaded_datetime || "Invalid date";
                      })()}
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-2 align-items-center">

                  <Button
                    variant="text"
                    sx={{
                      borderRadius: "20px", // Rounded but not too much
                      bgcolor: "#f5f5f5",
                      color: "#414141",
                      fontSize: "14px", // 14px as in the default theme
                      textTransform: "capitalize",
                      "&:hover": {
                        background: "#f5f5f5",
                      },
                    }}
                    className="d-flex justify-content-center align-items-center gap-2 rounded-3 px-3 "
                    onClick={() => handleRenameOpen(item)}
                  >
                    <Edit />
                    Rename
                  </Button>
                  <Button
                    variant="text"
                    sx={{
                      borderRadius: "20px", // Rounded but not too much
                      bgcolor: "#f5f5f5",
                      color: "#414141",
                      fontSize: "14px", // 14px as in the default theme
                      textTransform: "capitalize",
                      "&:hover": {
                        background: "#f5f5f5",
                      },
                    }}
                    className="d-flex justify-content-center align-items-center gap-2 rounded-3 px-3 "
                    onClick={() => {
                      window.open(item?.metadata?.image_url);
                    }}
                  >
                    <Visibility />
                    View
                  </Button>

                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {documents?.length === 0 && (
        <div className="NoDataAvailable">
          {NoDataSvg}
          <p> No Documents Available</p>
        </div>
      )}
      {documents?.length > 0 && (
        <div className="d-flex justify-content-end mb-5 p-4">
          <Box
            sx={{
              "& .MuiPaginationItem-root": {
                "&.Mui-selected": {
                  backgroundColor: "#var(--primary-color)", // Selected page background color
                  color: "#fff", // Selected page text color
                },
                "&:hover": {
                  backgroundColor: "var(--primary-color)", // Hover background color
                },
              },
            }}
          >
            <Pagination
              count={totalPages ? totalPages : 1}
              page={page}
              onChange={handleChange}
              renderItem={(item) => <PaginationItem {...item} />}
            />
          </Box>
        </div>
      )}
    </div>
  );
}

export default Documents;
